import { request, type QueryParams } from './client';

/**
 * CSV / JSON exports are streamed by the BE on demand. They are fetched as a
 * blob through the normal pipeline (cookies + CSRF + refresh) and saved with
 * an FE-built filename: the BE's Content-Disposition header is not
 * CORS-exposed, so the browser can't read it cross-origin (FE plan §3.3).
 */

export function exportFilename(resource: string, extension: 'csv' | 'json', on: Date = new Date()): string {
  const y = on.getFullYear();
  const m = String(on.getMonth() + 1).padStart(2, '0');
  const d = String(on.getDate()).padStart(2, '0');
  return `${resource}-${y}-${m}-${d}.${extension}`;
}

export async function fetchExport(endpoint: string, query?: QueryParams): Promise<Blob> {
  return request<Blob>(endpoint, { method: 'GET', query, responseType: 'blob' });
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function downloadExport(
  endpoint: string,
  resource: string,
  query?: QueryParams,
  extension: 'csv' | 'json' = 'csv',
): Promise<void> {
  const blob = await fetchExport(endpoint, query);
  saveBlob(blob, exportFilename(resource, extension));
}

/**
 * The audit export streams, so the 200 and headers go out before the last row
 * is read: a mid-stream failure yields valid CSV with a success status and no
 * back half. The BE ends a complete file with `# END,<rowCount>` (BE reqs
 * §7.11). A file is complete only if that line is last AND its count matches
 * the data rows received.
 */
export interface SentinelCheck {
  complete: boolean;
  /** Data rows received (header and sentinel excluded). */
  rowsReceived: number;
  /** The count the sentinel claims, or null when there is no sentinel. */
  rowsDeclared: number | null;
}

export function checkAuditExportSentinel(csv: string): SentinelCheck {
  const records = splitCsvRecords(csv).filter((record) => record.length > 0);
  const last = records[records.length - 1] ?? '';
  // The BE's csv.Writer quotes the field because it contains a comma, so the
  // real line is `"# END,1432"`; accept it bare too.
  const match = /^"?# END,(\d+)"?$/.exec(last);
  if (!match?.[1]) {
    // No sentinel: everything after the header is data.
    return { complete: false, rowsReceived: Math.max(0, records.length - 1), rowsDeclared: null };
  }
  const rowsDeclared = Number(match[1]);
  const rowsReceived = Math.max(0, records.length - 2);
  return { complete: rowsReceived === rowsDeclared, rowsReceived, rowsDeclared };
}

/**
 * Splits CSV text into records. A newline inside a quoted field does not end
 * the record — the audit export carries verbatim Stripe JSON per row, so
 * splitting on newlines alone would miscount. `""` inside quotes is an
 * escaped quote.
 */
export function splitCsvRecords(csv: string): string[] {
  const records: string[] = [];
  let inQuotes = false;
  let start = 0;
  for (let i = 0; i < csv.length; i++) {
    const ch = csv[i];
    if (ch === '"') {
      if (inQuotes && csv[i + 1] === '"') {
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === '\n' && !inQuotes) {
      const end = i > start && csv[i - 1] === '\r' ? i - 1 : i;
      records.push(csv.slice(start, end));
      start = i + 1;
    }
  }
  if (start < csv.length) records.push(csv.slice(start));
  return records;
}
